import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { LuCircleCheck, LuCopy, LuExternalLink, LuPaperclip, LuPencil, LuTrash2, LuX } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import Drawer from '../../../components/ui/Drawer';
import IconButton from '../../../components/ui/IconButton';
import { EASE_OUT } from '../../../components/ui/motionPresets';
import { getTrackingUrl } from '../../../utils/shipments';
import { getCargoStatusOptions, isCargoOverdue } from '../../../utils/companyShipments';
import { BLOCK_TITLE } from '../../ProfilePage/components/ProfileUi';
import { DIRECTION_STYLES, STATUS_DOTS, STATUS_TONES } from './cargoUi';
import useCargoText from './useCargoText';

const DOCK_SURFACE = [
  'tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)]',
  'dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]',
].join(' ');

const Badges = ({ shipment }) => {
  const { label, directionText, statusText } = useCargoText();
  const direction = directionText(shipment.direction);
  const status = statusText(shipment.direction, shipment.status);
  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
      <Badge tone={DIRECTION_STYLES[direction.value].badge}>{direction.short}</Badge>
      <Badge tone={status.tone}>{status.label}</Badge>
      {isCargoOverdue(shipment) && <Badge tone="danger">{label('overdue')}</Badge>}
    </div>
  );
};

Badges.propTypes = { shipment: PropTypes.object.isRequired };

/** Bilgiler, stok kalemleri, dosyalar ve geçmiş. */
const DetailBody = ({ shipment }) => {
  const { label, directionText, statusText, carrierName, formatDate, formatDateTime, formatCost } = useCargoText();
  const direction = directionText(shipment.direction);
  const carrierLabel = carrierName(shipment.carrier);
  const trackingUrl = getTrackingUrl(shipment.carrier, shipment.trackingNumber);
  const items = shipment.items || [];
  const attachments = shipment.attachments || [];
  const history = [...(shipment.history || [])].reverse();
  const stockApplied = (shipment.appliedItems || []).length > 0;

  const copyTracking = () =>
    navigator.clipboard
      ?.writeText(shipment.trackingNumber)
      .then(() => toast.success(label('toast.copied')))
      .catch(() => toast.error(label('toast.copyError')));

  // Boş alanlar listelenmez
  const info = [
    { key: 'party', label: direction.party, value: [shipment.partyName, shipment.partyOrg].filter(Boolean).join(' · '), wide: true },
    { key: 'phone', label: label('detail.phone'), value: shipment.partyPhone && <a href={`tel:${shipment.partyPhone}`} className="tw-text-teal-700 hover:tw-underline dark:tw-text-teal-300">{shipment.partyPhone}</a> },
    { key: 'carrier', label: label('detail.carrier'), value: !shipment.trackingNumber && carrierLabel },
    { key: 'cost', label: label('detail.cost'), value: formatCost(shipment.cost) },
    { key: 'shipped', label: label('detail.shipped'), value: formatDate(shipment.shippedAt) },
    { key: 'expected', label: label('detail.expected'), value: formatDate(shipment.expectedAt) },
    { key: 'delivered', label: label('detail.delivered'), value: formatDate(shipment.deliveredAt) },
    { key: 'owner', label: label('detail.owner'), value: shipment.ownerName },
    { key: 'address', label: direction.address, value: shipment.address, wide: true },
    { key: 'note', label: label('detail.note'), value: shipment.note, wide: true },
  ].filter((row) => row.value);

  return (
    <div className="tw-space-y-5">
      {shipment.content && (
        <p className="tw-whitespace-pre-line tw-text-sm tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">{shipment.content}</p>
      )}

      {shipment.trackingNumber && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50/80 tw-py-2 tw-pl-3.5 tw-pr-2 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
          <div className="tw-min-w-0 tw-flex-1">
            <p className="tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{carrierLabel || label('detail.trackingNo')}</p>
            <code className="tw-block tw-truncate tw-font-mono tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{shipment.trackingNumber}</code>
          </div>
          <IconButton label={label('detail.copy')} onClick={copyTracking}>
            <LuCopy aria-hidden="true" className="tw-h-4 tw-w-4" />
          </IconButton>
          {trackingUrl && (
            <a
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tw-inline-flex tw-h-10 tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-teal-500/10 tw-px-3.5 tw-text-sm tw-font-semibold tw-text-teal-700 tw-transition-colors hover:tw-bg-teal-500/15 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-teal-300"
            >
              {label('detail.query')}
              <LuExternalLink aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            </a>
          )}
        </div>
      )}

      {info.length > 0 && (
        <section className="tw-space-y-2.5">
          <h3 className={BLOCK_TITLE}>{label('detail.info')}</h3>
          <dl className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3">
            {info.map((row) => (
              <div key={row.key} className={`tw-min-w-0 ${row.wide ? 'tw-col-span-2' : ''}`}>
                <dt className="tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{row.label}</dt>
                <dd className="tw-mt-0.5 tw-whitespace-pre-line tw-break-words tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {items.length > 0 && (
        <section className="tw-space-y-2.5">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
            <h3 className={BLOCK_TITLE}>{label('detail.items')}</h3>
            <Badge tone={stockApplied ? 'success' : 'neutral'}>
              {stockApplied && <LuCircleCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
              {stockApplied ? label('detail.applied') : label('detail.notApplied')}
            </Badge>
          </div>
          <ul className="tw-divide-y tw-divide-slate-200/80 tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200/80 dark:tw-divide-white/10 dark:tw-border-white/10">
            {items.map((item) => (
              <li key={item.stockId} className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-3.5 tw-py-2.5 tw-text-sm">
                <span className="tw-min-w-0 tw-truncate tw-text-slate-700 dark:tw-text-slate-200">{item.stockName}</span>
                <strong className={`tw-shrink-0 tw-tabular-nums ${direction.value === 'incoming' ? 'tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-text-sky-600 dark:tw-text-sky-300'}`}>
                  {direction.value === 'incoming' ? '+' : '−'}
                  {item.quantity}
                </strong>
              </li>
            ))}
          </ul>
        </section>
      )}

      {attachments.length > 0 && (
        <section className="tw-space-y-2.5">
          <h3 className={BLOCK_TITLE}>{label('detail.files')}</h3>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {attachments.map((file, index) => (
              <a
                key={file.storagePath || `${file.name}-${index}`}
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                title={file.name}
                className="tw-inline-flex tw-max-w-full tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/80 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-sky-700 tw-transition-colors hover:tw-bg-white hover:tw-underline dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-sky-300"
              >
                <LuPaperclip aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                <span className="tw-truncate">{file.name}</span>
              </a>
            ))}
          </div>
        </section>
      )}

      {history.length > 0 && (
        <section className="tw-space-y-2.5">
          <h3 className={BLOCK_TITLE}>{label('detail.history')}</h3>
          <ol className="tw-relative tw-space-y-3 tw-pl-5 before:tw-absolute before:tw-bottom-1.5 before:tw-left-[5px] before:tw-top-1.5 before:tw-w-px before:tw-bg-slate-200 dark:before:tw-bg-white/10">
            {history.map((entry, index) => {
              const entryStatus = statusText(shipment.direction, entry.status);
              return (
                <li key={`${entry.at}-${index}`} className="tw-relative">
                  <span aria-hidden="true" className={`tw-absolute -tw-left-5 tw-top-1 tw-h-[11px] tw-w-[11px] tw-rounded-full tw-ring-4 tw-ring-white dark:tw-ring-[#1f232b] ${STATUS_DOTS[entryStatus.tone]}`} />
                  <p className="tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{entryStatus.label}</p>
                  <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                    {formatDateTime(entry.at)}
                    {entry.by ? ` · ${entry.by}` : ''}
                  </p>
                </li>
              );
            })}
          </ol>
        </section>
      )}
    </div>
  );
};

DetailBody.propTypes = { shipment: PropTypes.object.isRequired };

/** Hızlı durum düğmeleri ve Sil / Düzenle. */
const DetailActions = ({ shipment, busy, onEdit, onDelete, onStatusChange }) => {
  const { label, statusText } = useCargoText();
  return (
    <div className="tw-space-y-3">
      <div className="tw-space-y-2">
        <p className="tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label('detail.quickStatus')}</p>
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {getCargoStatusOptions(shipment.direction)
            .filter((option) => option.value !== shipment.status)
            .map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onStatusChange(shipment, option.value)}
                disabled={busy}
                className={`tw-inline-flex tw-h-9 tw-items-center tw-rounded-full tw-border tw-px-3.5 tw-text-xs tw-font-semibold tw-transition-[filter,transform] hover:tw-brightness-95 active:tw-scale-[0.97] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 dark:hover:tw-brightness-125 ${STATUS_TONES[option.tone]}`}
              >
                {statusText(shipment.direction, option.value).label}
              </button>
            ))}
        </div>
        <p className="tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{label('detail.quickStatusHint')}</p>
      </div>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-slate-200/80 tw-pt-3 dark:tw-border-white/10">
        <Button variant="danger" onClick={() => onDelete(shipment)} disabled={busy}>
          <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
          {label('detail.delete')}
        </Button>
        <Button variant="primary" onClick={() => onEdit(shipment)} disabled={busy} className="max-sm:tw-flex-1">
          <LuPencil aria-hidden="true" className="tw-h-4 tw-w-4" />
          {label('detail.edit')}
        </Button>
      </div>
    </div>
  );
};

DetailActions.propTypes = {
  shipment: PropTypes.object.isRequired,
  busy: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
};

/**
 * Seçili kargonun ayrıntıları. `docked`: listenin yanında sabit bir sütun
 * (geniş ekran; satırlar tıklanabilir kalır). Aksi halde sağdan açılan panel.
 */
const CargoDetailDrawer = ({ shipment, docked, canEdit, busy, onClose, onEdit, onDelete, onStatusChange }) => {
  const { language, label } = useCargoText();
  // Kapanış animasyonu sürerken son kayıt gösterilmeye devam eder
  const lastShipment = useRef(shipment);
  if (shipment) lastShipment.current = shipment;
  const shown = shipment || lastShipment.current;

  const actions = canEdit && shown && (
    <DetailActions shipment={shown} busy={busy} onEdit={onEdit} onDelete={onDelete} onStatusChange={onStatusChange} />
  );

  if (docked) {
    return (
      <AnimatePresence initial={false}>
        {shipment && (
          <motion.aside
            key="cargo-detail"
            aria-label={label('detail.label')}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            className={`tw-sticky tw-top-4 tw-flex tw-max-h-[calc(100vh-2rem)] tw-w-[26rem] tw-shrink-0 tw-flex-col tw-overflow-hidden tw-rounded-[1.5rem] ${DOCK_SURFACE}`}
          >
            <div className="tw-flex tw-shrink-0 tw-items-start tw-gap-3 tw-border-b tw-border-slate-200/80 tw-px-5 tw-py-4 dark:tw-border-white/10">
              <div className="tw-min-w-0 tw-flex-1 tw-space-y-1.5">
                <Badges shipment={shown} />
                <h2 className="tw-break-words tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{shown.subject}</h2>
              </div>
              <IconButton label={label('detail.close')} onClick={onClose}>
                <LuX aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
              </IconButton>
            </div>
            <motion.div
              key={shown.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.18 }}
              className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4 glass-scrollbar"
            >
              <DetailBody shipment={shown} />
            </motion.div>
            {actions && (
              <div className="tw-shrink-0 tw-border-t tw-border-slate-200/80 tw-bg-slate-50/70 tw-px-5 tw-py-3.5 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
                {actions}
              </div>
            )}
          </motion.aside>
        )}
      </AnimatePresence>
    );
  }

  return (
    <Drawer
      isOpen={Boolean(shipment)}
      onClose={onClose}
      title={shown?.subject || ''}
      eyebrow={shown && <Badges shipment={shown} />}
      closeLabel={label('detail.close')}
      footer={actions}
      lang={language}
    >
      {shown && <DetailBody shipment={shown} />}
    </Drawer>
  );
};

CargoDetailDrawer.propTypes = {
  shipment: PropTypes.object,
  docked: PropTypes.bool,
  canEdit: PropTypes.bool,
  busy: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
};

export default CargoDetailDrawer;
